"use client";;
import { font_text_varela } from "@/app/ui/fonts";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuLabel, ContextMenuSeparator, ContextMenuTrigger } from "@/app/ui/imported/context-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/app/ui/imported/tooltip";
import CopyContent from "@/app/ui/utils/CopyContent";
import Image from "@/app/ui/utils/Image";
import { cn, convertMsToISO8601, GoogleAnalyticsEvent, youtube_getVideoLink, youtube_getVideoThumbnail, youtube_isVideoRecent } from "@/lib/utils";
import { Video } from "@prisma/client";
import Link from "next/link";
import Script from "next/script";
import { CSSProperties, MouseEvent, ReactNode, useRef } from "react";
import { AiFillYoutube } from "react-icons/ai";
import { BiMoviePlay } from "react-icons/bi";
import { FaClock, FaCommentDots, FaEye, FaThumbsUp, FaUserAlt } from "react-icons/fa";
import { IoIosCopy } from "react-icons/io";
import { VideoObject, WithContext } from "schema-dts";
import "./VideoItem.css";

export default function VideoItem({ video, textBelow = false, style }: { video: Video, textBelow?: boolean, style?: CSSProperties }) {
    const firstClick = useRef(true)
    function handleClick(e: MouseEvent<HTMLAnchorElement>) {
        //track only leftClick(1) & wheel Click(2)
        if (e.button >= 2)
            return;
        //prevent multiple GA events
        if (!firstClick.current)
            return;
        firstClick.current = false;
        GoogleAnalyticsEvent({
            event_name: "videoView",
            event_label: video.id,
        });
    };

    const ytLink = youtube_getVideoLink(video.id);
    const isRecentVideo = youtube_isVideoRecent(video);

    const jsonLd: WithContext<VideoObject> = {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: video.title,
        description: `צפו עכשיו בפרק - ${video.title}`,
        thumbnailUrl: youtube_getVideoThumbnail(video.id),
        contentUrl: youtube_getVideoLink(video.id),
        duration: convertMsToISO8601(video.duration * 1000),
        uploadDate: new Date(video.createdAt).toISOString(),
        interactionStatistic: [{
            "@type": "InteractionCounter",
            interactionService: {
                "@type": "WebSite",
                "name": "YouTube",
                "@id": "https://youtube.com"
            },
            interactionType: { "@type": "LikeAction" },
            userInteractionCount: video.likes
        },
        {
            "@type": "InteractionCounter",
            interactionService: {
                "@type": "WebSite",
                "name": "YouTube",
                "@id": "https://youtube.com"
            },
            interactionType: { "@type": "WatchAction" },
            userInteractionCount: video.views
        }
        ]
    }


    return (
        <VideoContext { ...{ ytLink, handleClick, video } }>
            <Link href={ ytLink }
                target="_blank"
                className="btn flex group videoItem transition-transform duration-500 flex-col justify-stretch h-full"
                onClick={ handleClick } // Logs the event
                onAuxClick={ handleClick }
                style={ style }
            >
                <div className={ cn(" relative rounded-lg overflow-hidden aspect-video bg-gray-900 shadow-md ",
                    "hover:shadow-2xl hover:z-10", isRecentVideo && " outline-yellow-400 outline outline-4"
                ) }>

                    <Image
                        fill
                        sizes="20vw"
                        src={ youtube_getVideoThumbnail(video.id) }
                        alt={ video.title }
                        className="w-full h-full object-cover rounded-[inherit] group-hover:blur-[2px] transition-all duration-500 "
                    />
                    <div className={ cn("absolute inset-0  rounded-[inherit] from-transparent via-transparent to-transparent bg-gradient-to-t flex flex-col justify-between p-1 transition-all duration-300",
                        "group-hover:from-black/90 group-hover:via-black/70"
                    ) }>
                        { !textBelow && <Title className="group-hover:bg-transparent text-white">{ video.title }</Title> || <span /> }
                        <div className="p-1 pb-0 text-white font-black text-shadow-sm text-xs sm:text-lg flex justify-between">
                            <div className="flex flex-col items-stretch gap-1 sm:gap-2 justify-between">
                                <StatView tooltip="צפיות">
                                    <FaEye />
                                    <span>{ video.views.toLocaleString() }</span>
                                </StatView>
                                <StatView tooltip="תגובות" side="left"
                                    className="overflow-hidden transition-all duration-500 outline-0 group-hover:outline-1 max-h-0 group-hover:max-h-[2em]">
                                    <FaCommentDots />
                                    <span>{ video.comments }</span>
                                </StatView>
                            </div>
                            <div className="flex flex-col items-stretch gap-1 sm:gap-2 justify-between">
                                <StatView tooltip="לייקים">
                                    <FaThumbsUp />
                                    <span>{ video.likes.toLocaleString() }</span>
                                </StatView>
                                <StatView tooltip="זמן צפייה" side="right"
                                    className="overflow-hidden transition-all duration-500 outline-0 group-hover:outline-1 max-h-0 group-hover:max-h-[2em]">
                                    <FaClock />
                                    <span>{ Math.round(video.duration / 60).toLocaleString() }m</span>
                                </StatView>
                            </div>
                        </div>
                    </div>
                </div>
                { textBelow && <Title className="mt-2 line-clamp-2 text-white flex-1">{ video.title }</Title> }
            </Link>
            <Script
                id="videoScheme"
                type="application/ld+json"
                dangerouslySetInnerHTML={ { __html: JSON.stringify(jsonLd) } }
            />
        </VideoContext>
    );
}

function Title({ children, className }: { children: ReactNode, className?: string }) {
    return <h3 role="heading" className={ cn("text-white font-bold tracking-wide px-1 text-sm sm:text-md bg-black/30 rounded-md line-clamp-1 group-hover:line-clamp-2 text-shadow-sm text-shadow-black leading-[1.2em_!important]",
        font_text_varela.className, className
    ) }>
        { children }
    </h3>
}


function VideoContext({ children, ytLink, handleClick, video }: { children: ReactNode, ytLink: string, video: Video, handleClick: (e: MouseEvent<HTMLAnchorElement>) => void }) {
    return <ContextMenu>
        <ContextMenuTrigger>
            <>{ children }</>
        </ContextMenuTrigger>
        <ContextMenuContent>
            <ContextMenuLabel asChild className="py-0 px-0 text-2xl text-center"><h4>פעולות</h4></ContextMenuLabel>
            <ContextMenuSeparator />
            <ContextMenuItem asChild>
                <Link href={ ytLink } target="_blank" className="cursor-pointer" onClick={ handleClick }>
                    <AiFillYoutube className="me-2" size={ "1.5em" } /> פתח ביוטיוב
                </Link>
            </ContextMenuItem>
            <ContextMenuItem asChild>
                <Link href={ `/seasons/${video.seasonId}` } className="cursor-pointer">
                    <BiMoviePlay className="me-2" size={ "1.5em" } /> עבור לעונה
                </Link>
            </ContextMenuItem>
            <ContextMenuItem asChild>
                <Link href={ `/creator/${video.ytid}` } className="cursor-pointer">
                    <FaUserAlt className="me-2" size={ "1.5em" } /> עבור ליוצר
                </Link>
            </ContextMenuItem>
            <ContextMenuSeparator />
            <CopyContent as={ ContextMenuItem } asChild copy={ video.title }>
                <div className="cursor-copy">
                    <IoIosCopy className="me-2" size={ "1.5em" } /> העתק כותרת
                </div>
            </CopyContent>
        </ContextMenuContent>
    </ContextMenu>
}

function StatView({ children, className, tooltip, side = "top" }: { children: ReactNode, className?: string, tooltip: string, side?: "top" | "right" | "bottom" | "left" | undefined }) {
    return <Tooltip>
        <TooltipTrigger asChild>
            <p className={ cn("flex items-center justify-center text-center gap-0.5 sm:gap-2 bg-black/50 backdrop-blur-sm rounded px-1 outline-nfPurple-600 outline-1 outline shadow-lg shadow-black/80", className) }>
                { children }
            </p>
        </TooltipTrigger>
        <TooltipContent sideOffset={ side == "right" && 8 || side == "left" && -6 || -1 } side={ side }>
            { tooltip }
        </TooltipContent>
    </Tooltip>
}